import { EUserType } from './accessControll.types';
import type { AgPermission } from '@hubertai/screening-question-types';

export type TScoringLanguagePreference = {
  [key: string]: any;
  'sv-se': 'sv-se' | 'en';
  'en-gb': 'en-gb' | 'en';
  'en-us': 'en-us' | 'en';
  'da-dk': 'da-dk' | 'en';
  'no-no': 'no-no' | 'en';
  'fr-fr': 'fr-fr' | 'en';
  'de-de': 'de-de' | 'en';
  'es-es': 'es-es' | 'en';
  'es-mx': 'es-mx' | 'en';
  'uk-ua': 'uk-ua' | 'en';
  'ru-ru': 'ru-ru' | 'en';
};

type UserRole = 'admin' | 'recruiter' | 'recruiter-admin' | 'jacob-admin';

type BaseUser = {
  _id: string;
  email: string;
  first_name: string;
  last_name: string;
  fullname: string;
  userType: EUserType;
  userRoles?: UserRole[];
};
export type TAdminUser = BaseUser & {
  firstName?: string;
  lastName?: string;
  accessToken?: string;
  scoring_language_preference?: TScoringLanguagePreference;
};

type AccessGroupPermission = {
  accessGroupId: string;
  accessGroupName: string;
  resources: {
    resourceId: string;
    type: 'template' | 'question';
  }[];
  permissions: AgPermission[];
};

export type TRecruiterAppUser = BaseUser & {
  client_id: string;
  flag: string;
  pinned_jobs: string[];
  userRole: string;
  latest_activity_at?: Date;
  latest_login_at?: Date;
  isAdmin?: boolean;
  access_groups_permissions?: AccessGroupPermission[];
};

type TUser = TAdminUser | TRecruiterAppUser | undefined | null;

/**
 * Returns true if any of the user's access groups carry the given permission.
 * Role-level check — does not consider which specific resources are granted.
 */
export function userHasPermission(user: TUser, permission: AgPermission): boolean {
  if (!isRecruiterUser(user)) return false;
  return user.access_groups_permissions?.some((ag) => ag.permissions.includes(permission)) ?? false;
}

/**
 * Returns true if any of the user's access groups carry the given permission
 * AND explicitly grant access to the given resource.
 * Resource-level check.
 */
export function userHasResourcePermission(
  user: TUser,
  permission: AgPermission,
  resourceId: string,
  resourceType?: 'template' | 'question',
): boolean {
  if (!isRecruiterUser(user)) return false;
  return (
    user.access_groups_permissions?.some(
      (ag) =>
        ag.permissions.includes(permission) &&
        ag.resources.some((r) => r.resourceId === resourceId && (!resourceType || r.type === resourceType)),
    ) ?? false
  );
}

export function isRecruiterUser(user: TAdminUser | TRecruiterAppUser | undefined | null): user is TRecruiterAppUser {
  if (!user) {
    return false;
  }
  return user.userType === EUserType.RECRUITER;
}

export function isHubertAdminUser(user: TAdminUser | TRecruiterAppUser | undefined | null): user is TAdminUser {
  if (!user) {
    return false;
  }
  return user.userType === EUserType.HUBERT;
}

export function isDeveloper(user: TAdminUser | TRecruiterAppUser | undefined | null): user is TAdminUser {
  if (!user) {
    return false;
  }
  return (
    user.userType === EUserType.HUBERT &&
    [
      '6023a48c8f7f26095d316bff',
      '6474529a5e384551b44c236d',
      '6790e7a5be1df7629a1ee8d7',
      '689097f57795a4b61de22abd',
      '68d0f8928e5c8582e20329e5',
      '68d3c83a28c8ee6080d5f3ed',
      '60745d65e9ce6068d7e17f68',
      '68ef36b6676c4242ec6bbf7a',
      '689c53e12ef1f175bd86e339',
      '6023a4a98f7f26095d316c02',
      '68d3c83a28c8ee6080d5f3ed',
    ].includes(user._id)
  );
}
